import { StorageItem, User } from "@/Features";
import { CustomField } from "@/Features/CustomFields/custom-fields.types";
import { ImportFieldDefinition } from "../../_components/DataImport";
import { resolveDateBoxValue } from "../../_components/DataImport/utils";
import {
  ContactItem,
  EvidenceItem,
  FormatOption,
  HashTypeOption,
  TypeOption,
} from "./acquisitionImport.types";
import {
  mapCustomFieldEditorType,
  parseTagBoxCsvValue,
  resolveCapacityUnitValue,
  resolveCapacityValue,
} from "./acquisitionImport.utils";

interface CreateAcquisitionCoreFieldsOptions {
  users: User[];
  evidenceItems: EvidenceItem[];
  contacts: ContactItem[];
  storageItems: StorageItem[];
  formats: FormatOption[];
  types: TypeOption[];
  hashTypes: HashTypeOption[];
}

export function createAcquisitionCoreFields({
  users,
  evidenceItems,
  contacts,
  storageItems,
  formats,
  types,
  hashTypes,
}: CreateAcquisitionCoreFieldsOptions): ImportFieldDefinition[] {
  const userOptions = users.map((user) => ({
    value: user.user_id,
    label: user.full_name,
    data: user,
  }));

  const evidenceOptions = evidenceItems.map((e) => ({
    value: e.evidence_id,
    label: e.evidence_number,
  }));

  const contactOptions = contacts.map((c) => ({
    value: c.client_id,
    label: c.name,
  }));

  const storageOptions = storageItems.map((s) => ({
    value: s.storage_id as number,
    label: s.storage_number as string,
  }));

  const formatOptions = formats.map((f) => ({
    value: f.format,
    label: f.format,
  }));

  const typeOptions = types.map((t) => ({
    value: t.type,
    label: t.type,
  }));

  const hashTypeOptions = hashTypes.map((h) => ({
    value: h.hash_type,
    label: h.hash_type,
  }));

  const sizeUnitOptions = [
    { value: "B", label: "B" },
    { value: "KB", label: "KB" },
    { value: "MB", label: "MB" },
    { value: "GB", label: "GB" },
    { value: "TB", label: "TB" },
    { value: "PB", label: "PB" },
  ];

  return [
    {
      key: "acq_name",
      label: "Acquisition Name",
      type: "text",
      required: true,
      description: "Name for this acquisition.",
      aliases: ["name", "acquisition name", "acq name", "acquisition"],
    },
    {
      key: "evidence_id",
      label: "Evidence Item",
      type: "select",
      description: "Evidence item being acquired (matched by evidence number).",
      aliases: [
        "evidence",
        "evidence number",
        "evidence item",
        "evidence #",
        "evi #",
      ],
      options: evidenceOptions,
      displayValue: (value) => {
        if (value === null || value === undefined || value === "") return value;
        const id = typeof value === "number" ? value : Number(String(value));
        if (Number.isNaN(id)) return value;
        const match = evidenceItems.find((e) => e.evidence_id === id);
        return match ? match.evidence_number : value;
      },
      resolver: (val) => {
        const trimmed = String(val).trim();
        if (!trimmed) return undefined;

        const lower = trimmed.toLowerCase();
        const match = evidenceItems.find(
          (e) => e.evidence_number?.toLowerCase() === lower
        );
        return match?.evidence_id ?? undefined;
      },
    },
    {
      key: "format",
      label: "Format",
      type: "select",
      description: "Acquisition image format (e.g., E01, DD, RAW).",
      aliases: ["format", "image format", "acq format", "acquisition format"],
      options: formatOptions,
      resolver: (val) => {
        const lower = String(val).toLowerCase().trim();
        const match = formats.find((f) => f.format.toLowerCase() === lower);
        return match ? match.format : undefined;
      },
    },
    {
      key: "acq_type",
      label: "Acquisition Type",
      type: "select",
      description:
        "Type of acquisition (e.g., Physical, Logical, File System).",
      aliases: ["type", "acquisition type", "acq type"],
      options: typeOptions,
      resolver: (val) => {
        const lower = String(val).toLowerCase().trim();
        const match = types.find((t) => t.type.toLowerCase() === lower);
        return match ? match.type : undefined;
      },
    },
    {
      key: "acq_tool",
      label: "Forensic Software",
      type: "text",
      description: "Forensic software used for the acquisition.",
      aliases: [
        "tool",
        "software",
        "forensic software",
        "software name",
        "acquisition tool",
        "acq tool",
      ],
    },
    {
      key: "tool_version",
      label: "Software Version",
      type: "text",
      description: "Version of the forensic software used.",
      aliases: ["version", "software version", "tool version"],
    },
    {
      key: "size",
      label: "Size",
      type: "number",
      description: "Size of the acquisition.",
      aliases: ["size", "file size", "acquisition size", "capacity"],
      resolver: (val) => resolveCapacityValue(val),
    },
    {
      key: "size_unit",
      label: "Size Unit",
      type: "select",
      description: "Unit for size (B, KB, MB, GB, TB, PB).",
      aliases: ["unit", "size unit", "capacity unit"],
      options: sizeUnitOptions,
      resolver: (val) => {
        const extractedUnit = resolveCapacityUnitValue(val);
        if (extractedUnit) {
          return extractedUnit;
        }

        const upper = val.toUpperCase().trim();
        const match = sizeUnitOptions.find((o) => o.value === upper);
        return match ? match.value : undefined;
      },
    },
    {
      key: "hash_1",
      label: "Hash 1",
      type: "text",
      description: "Primary hash value.",
      aliases: ["hash 1", "hash1", "hash", "primary hash", "hash value"],
    },
    {
      key: "hash_1_type",
      label: "Hash 1 Algorithm",
      type: "select",
      description: "Algorithm for Hash 1 (e.g., MD5, SHA1, SHA256).",
      aliases: [
        "hash 1 type",
        "hash 1 algorithm",
        "hash1 type",
        "hash type",
        "algorithm",
      ],
      options: hashTypeOptions,
      resolver: (val) => {
        const lower = val.toLowerCase().trim();
        const match = hashTypes.find(
          (h) => h.hash_type.toLowerCase() === lower
        );
        return match ? match.hash_type : undefined;
      },
    },
    {
      key: "hash_2",
      label: "Hash 2",
      type: "text",
      description: "Secondary hash value.",
      aliases: ["hash 2", "hash2", "secondary hash", "hash value 2"],
    },
    {
      key: "hash_2_type",
      label: "Hash 2 Algorithm",
      type: "select",
      description: "Algorithm for Hash 2.",
      aliases: [
        "hash 2 type",
        "hash 2 algorithm",
        "hash2 type",
        "second algorithm",
      ],
      options: hashTypeOptions,
      resolver: (val) => {
        const lower = val.toLowerCase().trim();
        const match = hashTypes.find(
          (h) => h.hash_type.toLowerCase() === lower
        );
        return match ? match.hash_type : undefined;
      },
    },
    {
      key: "acquire_date",
      label: "Acquire Date",
      type: "date",
      description: "Date the acquisition was performed.",
      aliases: ["date", "acquire date", "acquisition date", "date acquired"],
      resolver: (val) => resolveDateBoxValue(String(val)),
    },
    {
      key: "duration_hours",
      label: "Duration Hours",
      type: "number",
      description: "Duration of the acquisition in hours.",
      aliases: ["hours", "duration hours", "duration (hours)"],
    },
    {
      key: "duration_mins",
      label: "Duration Minutes",
      type: "number",
      description: "Duration of the acquisition in minutes.",
      aliases: [
        "minutes",
        "mins",
        "duration minutes",
        "duration mins",
        "duration (mins)",
        "duration (minutes)",
      ],
    },
    {
      key: "acquired_by_id",
      label: "Acquired By",
      type: "select",
      description: "User who performed the acquisition.",
      aliases: ["acquired by", "examiner", "acquired by user"],
      options: userOptions,
      displayValue: (value) => {
        if (value === null || value === undefined || value === "") return value;
        const id = typeof value === "number" ? value : Number(String(value));
        if (Number.isNaN(id)) return value;
        const match = users.find((u) => u.user_id === id);
        return match ? match.full_name : value;
      },
      validate: (_val, _row, rawValue) => {
        if (!rawValue) return null;
        const lower = rawValue.toLowerCase().trim();

        const numericId = Number(rawValue);
        const matchesById =
          !Number.isNaN(numericId) &&
          userOptions.some((o) => o.value === numericId);
        const matchesByEmail = userOptions.some(
          (o) => o.data?.email?.toLowerCase() === lower
        );
        if (matchesById || matchesByEmail) return null;

        const nameMatches = userOptions.filter(
          (o) => o.label.toLowerCase() === lower
        );
        if (nameMatches.length > 1) {
          return "Multiple users found for the same name";
        }
        return null;
      },
      resolver: (val) => {
        const lower = val.toLowerCase().trim();

        const match = userOptions.find(
          (o) =>
            o.value === Number(val) ||
            o.data?.email?.toLowerCase() === lower ||
            o.label.toLowerCase() === lower
        );
        return match ? match.value : undefined;
      },
    },
    {
      key: "person_id",
      label: "Linked Contact",
      type: "select",
      description: "Contact linked to this acquisition.",
      aliases: ["contact", "linked contact", "contact name"],
      options: contactOptions,
      unresolvedWarning: (rawValue) =>
        `Linked Contact "${rawValue}" does not match any contacts in this case`,
      displayValue: (value) => {
        if (value === null || value === undefined || value === "") return value;
        const id = typeof value === "number" ? value : Number(String(value));
        if (Number.isNaN(id)) return value;
        const match = contacts.find((c) => c.client_id === id);
        return match ? match.name : value;
      },
      validate: (_val, _row, rawValue) => {
        if (!rawValue) return null;
        const lower = String(rawValue).toLowerCase().trim();

        const matchesByEmail = contacts.some(
          (c) => c.email?.toLowerCase() === lower
        );
        if (matchesByEmail) return null;

        const nameMatches = contacts.filter(
          (c) => c.name?.toLowerCase() === lower
        );
        if (nameMatches.length > 1) {
          return "Multiple contacts found for the same name";
        }
        return null;
      },
      resolver: (val) => {
        const trimmed = String(val).trim();
        if (!trimmed) return undefined;

        const lower = trimmed.toLowerCase();
        const match = contacts.find(
          (c) =>
            c.email?.toLowerCase() === lower || c.name?.toLowerCase() === lower
        );
        return match ? match.client_id : undefined;
      },
    },
    {
      key: "storage_id",
      label: "Storage Item",
      type: "select",
      description: "Storage item this acquisition is stored on.",
      aliases: ["storage", "storage item", "storage number"],
      options: storageOptions,
      displayValue: (value) => {
        if (value === null || value === undefined || value === "") return value;
        const id = typeof value === "number" ? value : Number(String(value));
        if (Number.isNaN(id)) return value;
        const match = storageItems.find((s) => s.storage_id === id);
        return match ? match.storage_number : value;
      },
      resolver: (val) => {
        const trimmed = String(val).trim();
        if (!trimmed) return undefined;

        const lower = trimmed.toLowerCase();
        const match = storageItems.find(
          (s) => s.storage_number?.toLowerCase() === lower
        );
        return match ? match.storage_id : undefined;
      },
    },
    {
      key: "acq_notes",
      label: "Notes",
      type: "text",
      description: "Notes or description for this acquisition.",
      aliases: ["notes", "description", "acquisition notes", "acq notes"],
    },
  ];
}

export function createAcquisitionCustomFields(
  customFields: CustomField[]
): ImportFieldDefinition[] {
  return customFields.map((cf) => {
    const fieldType = mapCustomFieldEditorType(cf.editor_type);

    const fieldDef: ImportFieldDefinition = {
      key: `custom_attribute_${cf.field_id}`,
      label: cf.field_name,
      type: fieldType,
      description: cf.description || undefined,
      required: cf.is_required === 1,
      aliases: [cf.field_name.toLowerCase()],
    };

    if (
      (cf.editor_type === "dropDown" || cf.editor_type === "tagBox") &&
      cf.options
    ) {
      fieldDef.options = cf.options.map((opt) => ({
        value: opt,
        label: opt,
      }));
    }

    if (cf.editor_type === "tagBox" && cf.options) {
      const optionLookup = new Map<string, string>();
      cf.options.forEach((opt) => {
        optionLookup.set(opt.toLowerCase(), opt);
      });

      fieldDef.resolver = (val) => {
        const tokens = parseTagBoxCsvValue(String(val));
        if (tokens.length === 0) return [];

        const resolvedValues: string[] = [];
        for (const token of tokens) {
          const resolvedOption = optionLookup.get(token.toLowerCase());
          if (resolvedOption !== undefined) {
            resolvedValues.push(resolvedOption);
          }
        }

        return Array.from(new Set(resolvedValues));
      };

      fieldDef.warn = (_value, _row, rawValue) => {
        if (!rawValue) return null;

        const tokens = parseTagBoxCsvValue(rawValue);
        const unmatched = tokens.filter(
          (token) => !optionLookup.has(token.toLowerCase())
        );
        if (unmatched.length === 0) return null;

        return `The following values do not match any options for ${
          cf.field_name
        } and will not be imported: ${unmatched.join(", ")}`;
      };

      fieldDef.displayValue = (value) => {
        if (!Array.isArray(value)) return value;
        return value.join(", ");
      };
    }

    if (cf.editor_type === "dateBox") {
      fieldDef.resolver = (val) => resolveDateBoxValue(String(val));
    }

    return fieldDef;
  });
}
